// src/components/page/property/PropertyPill.tsx
import { Box, Chip, Typography } from "@mui/material";
import React from "react";
import { useGetPropertyValue, useFolderPropertyTypes } from "../../../hook/page/property";
import { useViewSetting } from "../../../hook/page/viewSetting";
import { useSelectedDatabaseViewId } from "../../../hook/page/view";
import { PropertyTypeKey, propValueToText } from "../../../types/page/property";
import { PropertyTypeSelect } from "../../../types/page/propertyType/select";
import { resolveChipColor } from "../../../types/page/propertyColor";

export const PropertyPill: React.FC<{
  propName: string;
  rawValue: unknown;
  propType?: PropertyTypeKey;
  options?: PropertyTypeSelect["options"];
}> = ({ propName, rawValue, propType, options }) => {
  // One shared rule for every renderer — see propValueToText (types/page/property.ts).
  const value = propValueToText(rawValue);
  if (value === "") return null;

  if (propType === PropertyTypeKey.SELECT) {
    const opt = (options ?? []).find((o) => o.label === value);
    const { bg, fg } = resolveChipColor(opt?.color);
    return (
      <Chip
        size="small"
        label={value}
        data-testid={`pill-${propName}`}
        sx={{ bgcolor: bg, color: fg, height: 20, fontSize: "0.7rem" }}
      />
    );
  }
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      data-testid={`pill-${propName}`}
      sx={{ display: "block" }}
    >
      {value}
    </Typography>
  );
};

export const CardPropertyPills: React.FC<{
  pageId: string;
  folderId: string;
}> = ({ pageId, folderId }) => {
  const [selectedViewId] = useSelectedDatabaseViewId();
  const setting = useViewSetting(selectedViewId);
  const cardProps = setting?.["card-props"] ?? [];
  const getPropertyValue = useGetPropertyValue();
  const { folderProps } = useFolderPropertyTypes(folderId);
  const props = folderProps ?? {};

  if (cardProps.length === 0) return null;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
      {cardProps.map((name) => {
        const prop = props[name];
        if (!prop) return null; // race: prop deleted but config not yet refetched
        return (
          <PropertyPill
            key={name}
            propName={name}
            rawValue={getPropertyValue(pageId, name)}
            propType={prop["prop-type"]}
            options={(prop as PropertyTypeSelect).options}
          />
        );
      })}
    </Box>
  );
};
